import * as React from 'react';
import { useGridApiContext, GridEditInputCell, GridColDef } from '@mui/x-data-grid-pro';
import { InputBase, InputBaseProps, Paper, Popper } from '@mui/material';
import { handleGridCellChange } from './changeDetectionHelper';

const GridEditTextareaCell = React.memo(function GridEditTextareaCell(props: any) {
  const { id, field, value, colDef, hasFocus, row } = props;
  const [valueState, setValueState] = React.useState(value);
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>();
  const [expanded, setShouldExpand] = React.useState(false);
  const [inputRef, setInputRef] = React.useState<HTMLInputElement | null>(null);
  const apiRef = useGridApiContext();

  React.useLayoutEffect(() => {
    if (hasFocus && inputRef) {
      inputRef.focus();
    }
  }, [hasFocus, inputRef]);

  const handleRef = React.useCallback((el: HTMLElement | null) => {
    setAnchorEl(el);
  }, []);

  const handleChange = React.useCallback<NonNullable<InputBaseProps['onChange']>>(
    event => {
      const newValue = event.target.value;
      
      setValueState(newValue);
      
      // Use the helper function for change detection
      handleGridCellChange({
        id,
        field,
        newValue,
        oldValue: value,
        apiRef,
      });
      
      apiRef.current.setEditCellValue({ id, field, value: newValue, debounceMs: 200 }, event);
    },
    [apiRef, field, id, value],
  );

  return (
    <div
      style={{ position: 'relative', alignSelf: `${expanded ? 'flex-start' : 'center'}` }}
      onBlur={e => setShouldExpand(false)}
      onFocus={e => setShouldExpand(true)}
    >
      {expanded ? (
        <>
          <div
            ref={handleRef}
            style={{
              height: 1,
              width: colDef.computedWidth,
              display: 'block',
              position: 'absolute',
              top: 0,
            }}
          />
          {anchorEl && (
            <Popper open anchorEl={anchorEl} placement="bottom-start" sx={{ width: colDef.computedWidth }}>
              <Paper elevation={1} sx={{ p: 1 }}>
                <InputBase
                  multiline
                  rows={4}
                  value={valueState}
                  sx={{ textarea: { resize: 'both' }, width: '100%' }}
                  onChange={handleChange}
                  inputRef={ref => setInputRef(ref)}
                  disabled={row?.isTimeOff}
                />
              </Paper>
            </Popper>
          )}
        </>
      ) : (
        <GridEditInputCell {...props} sx={{ input: { paddingX: '10px' } }} disabled={row?.isTimeOff} />
      )}
    </div>
  );
});

export const renderEditTextareaCell: GridColDef['renderCell'] = params => {
  return <GridEditTextareaCell {...params} />;
};
